import deepEqual from 'deep-equal'
import { array, bool, func, oneOf, string } from 'prop-types'
import { Component } from 'react'
import { preventDefault } from '../../utils'
import FileBrowserView from '../FileBrowserView'
import { findCurrentTree } from './utils'

import './styles.scss'

const isUpOrDownKey = (event) =>
  event.keyCode && (event.keyCode === 38 || event.keyCode === 40)

const onKeyDown = (event) => {
  if (isUpOrDownKey(event)) {
    preventDefault(event)
  }
}

export default class RawFilterBar extends Component {
  static propTypes = {
    currentPath: array,
    error: string,
    fileTree: array,
    lastPath: array,
    loaded: bool,
    onBlur: func,
    onFileToggle: func,
    onFocus: func,
    onPathChange: func,
    theme: oneOf(['dark', 'light']),
    value: array,
  }

  constructor(props) {
    super(props)

    this.loadProps(props)

    this.state = {}
    this.state = {
      focused: this.focused,
      highlightedFileID: '',
    }

    this.focus = this.focus.bind(this)
    this.blur = this.blur.bind(this)
    this.onKeyUp = this.onKeyUp.bind(this)
  }

  componentDidMount() {
    this.setHighlighted(0)
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    this.loadProps(nextProps)
  }

  focus() {
    this.setHighlighted(Math.max(this.previousIndex || 0, 0))
    this.setState(() => ({
      focused: true,
    }))

    if (this.props.onFocus) {
      this.props.onFocus()
    }
  }

  blur() {
    this.setHighlighted(-1)
    this.setState(() => ({
      focused: false,
    }))

    if (this.props.onBlur) {
      this.props.onBlur()
    }
  }

  onKeyUp(event) {
    if (event && event.keyCode) {
      switch (event.keyCode) {
        case 38: // up key
          event.preventDefault()
          this.decrementHighlighted()
          break
        case 40: // down key
          event.preventDefault()
          this.incrementHighlighted()
          break
        case 37: // left key
          this.traverseBack()
          break
        case 39: // right key
          this.traverseForward()
          break
        case 13: // enter key
          this.onEnterKey()
          break
        default:
          break
      }
    }
  }

  onEnterKey() {
    const selected = this.highlightedItem
    if (selected.id === this.currentPathTail) {
      this.traverseBack()
    } else {
      this.props.onFileToggle(selected)
    }
  }

  traverseBack() {
    const path = this.props.currentPath || []
    if (path && path.length) {
      this.props.onPathChange(path.slice(0, -1))
    }
  }

  traverseForward() {
    const file = this.highlightedItem
    const hasValidChildren =
      file && file.childOptions && file.childOptions.length
    if (hasValidChildren && file.id !== this.currentPathTail) {
      this.props.onPathChange([...(this.props.currentPath || []), file.id])
    }
  }

  loadProps(props) {
    const { currentPath } = props

    this.currentFileTree = findCurrentTree(props.currentPath, props.fileTree)

    if (!deepEqual(currentPath, this.props.currentPath)) {
      this.setHighlighted(this.getPathIndex(currentPath || []))
    }
  }

  setHighlighted(idx, cb) {
    this.previousIndex = this.index || 0
    this.index = idx
    const result = this.highlightedItem

    this.setState(
      {
        highlightedFileID: result ? result.id : '',
      },
      () => {
        if (cb) {
          cb.call(this)
        }
      }
    )
  }

  incrementHighlighted() {
    const len = this.currentFileTree ? this.currentFileTree.length - 1 : 0
    this.setHighlighted(Math.min(this.highlightIndex + 1, len))
  }

  decrementHighlighted() {
    this.setHighlighted(Math.max(0, this.highlightIndex - 1))
  }

  getPathIndex(newPath) {
    const oldPath = this.props.currentPath || []
    if (newPath.length > oldPath.length) {
      return 0
    }

    const tail = this.currentPathTail
    return Math.max(
      0,
      this.currentFileTree.findIndex((f) => f.id === tail)
    )
  }

  get focused() {
    const { focused = true } = this.state
    return Boolean(focused)
  }

  get highlightIndex() {
    if (this.index === undefined || Number.isNaN(this.index)) {
      this.index = -1
    }
    return this.index
  }

  get scrollToIndex() {
    if (this.currentFileTree && this.currentFileTree.length) {
      return this.highlightIndex
    }
    return null
  }

  get highlightedItem() {
    return this.currentFileTree[this.highlightIndex]
  }

  get currentPathTail() {
    const path = this.props.currentPath || []
    return path.length ? path.slice(-1)[0] : undefined
  }

  render() {
    const {
      currentPath = [],
      error,
      fileTree,
      lastPath,
      loaded,
      onFileToggle,
      onPathChange,
      theme = 'light',
      value,
    } = this.props
    const { highlightedFileID } = this.state
    const { focused } = this

    return (
      <FileBrowserView
        currentPath={currentPath}
        error={error}
        fileTree={fileTree}
        focused={focused}
        highlightedFileID={highlightedFileID}
        lastPath={lastPath}
        loaded={loaded}
        onBlur={this.blur}
        onFileToggle={onFileToggle}
        onFocus={this.focus}
        onKeyDown={onKeyDown}
        onKeyUp={this.onKeyUp}
        onPathChange={onPathChange}
        scrollToIndex={this.scrollToIndex}
        theme={theme}
        value={value}
      />
    )
  }
}
